import { LumenApplicationParameters, ReckittApplicationParameters } from '@platform/client/schemas';
import { FormsMultilineInput, RadioGroup } from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/repeatvolunteer.svg';

type FormValues = {
  repeatVolunteer: string;
  previousExperience?: string;
};

export const RepeatVolunteerSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress } = { ...props };

  const { updateApplicationData, getApplicationDataByKey } = useApplicationLocalStorage<
    LumenApplicationParameters | ReckittApplicationParameters
  >();

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      repeatVolunteer: getApplicationDataByKey('repeatVolunteer'),
      previousExperience: getApplicationDataByKey('previousExperience'),
    },
  });

  const submit: SubmitHandler<FormValues> = (data) => {
    updateApplicationData({
      repeatVolunteer: data.repeatVolunteer,
      previousVolunteerExperience: data.previousExperience || null,
    });
    onContinue();
  };

  const handleBack = () => {
    updateApplicationData({
      repeatVolunteer: watch('repeatVolunteer'),
      previousVolunteerExperience: watch('previousExperience'),
    });
    onBack();
  };

  return (
    <ApplicationBackground
      progress={progress}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
    >
      <ApplicationPageContent
        title='Have you volunteered through your current company?'
        description="This helps your company understand employee participation in volunteer programs. No worries if you haven't yet!"
      >
        <RadioGroup
          name='f5-repeat-volunteer-selection'
          aria-label='f5-repeat-volunteer-selection'
          register={() => {
            return register('repeatVolunteer', {
              required: 'Please select your volunteer experience to continue',
            });
          }}
          items={[
            {
              value: 'yes',
              title: "Yes, I've volunteered through my current company",
            },
            {
              value: 'no',
              title: 'Not yet, this would be my first time volunteering through my current company',
            },
          ]}
          onChange={(value) => {
            setValue('repeatVolunteer', value as string);
          }}
          defaultValue={getApplicationDataByKey('repeatVolunteer')}
          error={errors.repeatVolunteer?.message}
        />

        {watch('repeatVolunteer') === 'yes' && (
          <FormsMultilineInput
            label='What company-led volunteer programs have you participated in? (optional)'
            placeholder='Describe what volunteer programs you participated in'
            rules={{ maxLength: 500 }}
            characterCount={500}
            control={control}
            name='previousExperience'
            formsError={errors.previousExperience}
          />
        )}
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
